import React, { useMemo, useRef, useState } from 'react';
import {
  createColumnHelper,
  PageContainer,
  ZigInput,
  ZigTablePriceLabel,
  ZigSelect,
  ZigTable,
  ZigTableQueryRef,
  ZigTypography,
} from '@zignaly-open/ui';
import {
  useLazyDepositsCsvQuery,
  useDepositsQuery,
} from '../../apis/transfers/api';
import { useTranslation } from 'react-i18next';
import { ValueOrDash } from '../TableUtils/ValueOrDash';
import { Box } from '@mui/material';
import { DepositData, TransferFilterType } from '../../apis/transfers/types';
import { depositStatusColorMap } from './constants';
import { useDepositStatusOptions, useOperatorOptions } from './use';
import DateDisplay from '../TableUtils/DateDisplay';
import { isEqual as _isEqual } from 'lodash-es';
import Shorten from '../TableUtils/Shorten';
import FilterButtons from '../TableUtils/FilterButtons';
import TableWrap, { TableFilterWrap } from '../TableUtils/TableWrap';
import { convertUserInputToNumberFormat } from './utils';
import ExportButton from '../ExportButton';

const initialFilter: TransferFilterType = {
  userId: '',
  amount: '0',
  operator: 'gte',
  status: '',
};

export default function Deposits() {
  const { t } = useTranslation(['transfers', 'common']);

  const [errorInputId, setErrorInputId] = useState<string>('');

  const [filters, setFilters] = useState<TransferFilterType>(initialFilter);
  const [filtersSubmitted, setFiltersSubmitted] =
    useState<TransferFilterType>(filters);

  const ref = useRef<ZigTableQueryRef>(null as ZigTableQueryRef);
  const operators = useOperatorOptions();
  const statusOptions = useDepositStatusOptions();

  const columnHelper = createColumnHelper<DepositData>();
  const columns = useMemo(() => {
    return [
      columnHelper.accessor('createdAt', {
        header: t('table.date'),
        cell: ({ getValue }) => <DateDisplay date={getValue()} />,
      }),
      columnHelper.accessor('email', {
        enableSorting: false,
        header: t('table.user'),
        cell: ({ getValue, row }) => (
          <Box>
            <ZigTypography component={'p'}>{getValue()}</ZigTypography>
            <ZigTypography component={'p'} variant={'body2'}>
              {row.original.userId}
            </ZigTypography>
          </Box>
        ),
      }),
      columnHelper.accessor('amount', {
        header: t('table.amount'),
        cell: ({ row }) => (
          <ZigTablePriceLabel
            value={row.original.amount}
            coin={row.original.coin}
          />
        ),
      }),
      columnHelper.accessor('status', {
        header: t('table.status'),
        enableSorting: false,
        cell: ({ getValue }) => (
          <ValueOrDash color={depositStatusColorMap[getValue()]}>
            {getValue() && t('depositStatuses.' + getValue())}
          </ValueOrDash>
        ),
      }),
      columnHelper.accessor('fromAddress', {
        enableSorting: false,
        header: t('table.fromAddress'),
        cell: ({ getValue }) => (
          <Box display={'flex'} width={'100%'} justifyContent={'center'}>
            <Shorten
              typographyProps={{ sx: { minWidth: 150 }, variant: 'body2' }}
              breakLines
              text={getValue()}
              width={200}
            />
          </Box>
        ),
      }),
      columnHelper.accessor('toAddress', {
        enableSorting: false,
        header: t('table.toAddress'),
        cell: ({ getValue }) => (
          <Box display={'flex'} width={'100%'} justifyContent={'center'}>
            <Shorten
              typographyProps={{ sx: { minWidth: 150 }, variant: 'body2' }}
              breakLines
              text={getValue()}
              width={200}
            />
          </Box>
        ),
      }),
      columnHelper.accessor('transactionId', {
        enableSorting: false,
        header: t('table.transactionId'),
        cell: ({ getValue }) => (
          <Box display={'flex'} width={'100%'} justifyContent={'center'}>
            <Shorten
              typographyProps={{ sx: { minWidth: 150 }, variant: 'body2' }}
              breakLines
              text={getValue()}
              width={200}
            />
          </Box>
        ),
      }),
    ];
  }, [filters, ref]);

  return (
    <PageContainer
      style={{
        marginTop: '32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '0 50px',
      }}
    >
      <ZigTypography sx={{ mb: 4 }} variant={'h1'}>
        {t('navigation.deposits')}
      </ZigTypography>

      <TableFilterWrap
        sx={{
          width: 1000,
        }}
      >
        <ZigInput
          size={'small'}
          label={t('table.userId')}
          placeholder={t('table.userId')}
          value={filters.userId}
          onChange={(e) => {
            setErrorInputId('');
            setFilters((old) => ({ ...old, userId: e.target.value }));
          }}
          error={errorInputId}
        />
        <Box mt={'-2px'}>
          <ZigTypography fontSize={'13px'} lineHeight={'20px'}>
            {t('table.amount')}
          </ZigTypography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              mt: '5px',
              gap: '10px',
            }}
          >
            <ZigSelect
              small
              width={100}
              value={filters.operator}
              onChange={(operator) =>
                setFilters((old) => ({ ...old, operator }))
              }
              options={operators}
            />
            <ZigInput
              size={'small'}
              placeholder={t('table.amount')}
              value={filters.amount}
              onChange={(e) =>
                setFilters((old) => ({
                  ...old,
                  amount: convertUserInputToNumberFormat(
                    e.target.value,
                    old.amount,
                  ),
                }))
              }
            />
          </Box>
        </Box>
        <ZigSelect
          label={t('table.status')}
          small
          value={filters.status}
          onChange={(status) => setFilters((old) => ({ ...old, status }))}
          options={statusOptions}
        />
        <FilterButtons
          onClick={() => {
            if (
              !filters.userId.match(/^[0-9a-fA-F]{24}$/) &&
              filters.userId !== ''
            ) {
              setErrorInputId(t('error.invalid-value'));
              return;
            }
            if (_isEqual(filters, filtersSubmitted)) ref.current?.refetch();
            else setFiltersSubmitted(filters);
          }}
          onClear={() => {
            setFilters({ ...initialFilter });
            setFiltersSubmitted({ ...initialFilter });
          }}
        />
        <ExportButton
          useLazyExportCsvQuery={useLazyDepositsCsvQuery}
          filters={filtersSubmitted}
          fileName={'Deposits'}
        />
      </TableFilterWrap>

      <TableWrap>
        <ZigTable
          ref={ref}
          initialState={{
            sorting: [
              {
                id: 'createdAt',
                desc: true,
              },
            ],
          }}
          columns={columns}
          useQuery={useDepositsQuery}
          queryExtraParams={filtersSubmitted}
          enableSortingRemoval={false}
          emptyMessage={t('no-data')}
        />
      </TableWrap>
    </PageContainer>
  );
}
